
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";

import {
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  TeamOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";

import Swal from "sweetalert2";
import { pdf } from "@react-pdf/renderer";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";

import SalaryAdvancePDF from "./SalaryAdvancePDF";

const { Title, Text } = Typography;
const { TextArea } = Input;

const EMPTY_ADVANCE = {
  teacher: null,
  support_staff: null,
  amount: "",
  reason: "",
  month: "",
};

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ROWS_PER_PAGE = 8;


// ============================================
// HELPERS
// ============================================

const getResponseData = (response) => {
  const result = response?.data;

  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.results)) return result.results;
  if (Array.isArray(result?.data)) return result.data;

  return [];
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatMoney = (value) =>
  `$${toNumber(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getStaffName = (row) =>
  row?.teacher_name ||
  row?.support_staff_name ||
  "-";

const getStaffType = (row) => {
  if (row?.teacher_name || row?.teacher) return "Teacher";
  if (row?.support_staff_name || row?.support_staff) {
    return "Support Staff";
  }
  return "Unspecified";
};

const getAdvanceStatus = (row) => {
  if (row?.status) return row.status;

  return toNumber(row?.remaining_balance) > 0
    ? "Pending"
    : "Fully Taken";
};

const getErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (!responseData) {
    return error?.message || "Something went wrong. Please try again.";
  }

  if (typeof responseData === "string") {
    return responseData;
  }

  if (responseData.detail) return responseData.detail;
  if (responseData.error) return responseData.error;

  return Object.entries(responseData)
    .map(([field, messages]) => {
      const value = Array.isArray(messages)
        ? messages.join(", ")
        : typeof messages === "object"
          ? JSON.stringify(messages)
          : String(messages);

      return `${field}: ${value}`;
    })
    .join("\n");
};


// ============================================
// MAIN COMPONENT
// ============================================

const SalaryAdvance = () => {
  const { selectedYear } = useAcademicYear();

  const [open, setOpen] = useState(false);

  const [teacherList, setTeacherList] = useState([]);
  const [supportStaffList, setSupportStaffList] = useState([]);
  const [data, setData] = useState([]);

  const [advance, setAdvance] = useState({ ...EMPTY_ADVANCE });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [filterText, setFilterText] = useState("");
  const [editId, setEditId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [page, setPage] = useState(1);
  const [reportMonth, setReportMonth] = useState("");

  const requestCounter = useRef(0);

  const academicYearName =
    selectedYear?.name ||
    selectedYear?.academic_year ||
    "-";


  // ============================================
  // RESET FORM
  // ============================================

  const resetForm = useCallback(() => {
    setAdvance({ ...EMPTY_ADVANCE });
    setEditId(null);
  }, []);


  // ============================================
  // FETCH DATA
  // ============================================

  const fetchInitialData = useCallback(
    async ({ showError = true } = {}) => {
      if (!selectedYear?.id) {
        setTeacherList([]);
        setSupportStaffList([]);
        setData([]);
        setFetching(false);
        return false;
      }

      const requestId = ++requestCounter.current;

      setFetching(true);

      try {
        const [
          teachersResponse,
          staffResponse,
          advancesResponse,
        ] = await Promise.all([
          api.get("/api/users/teacher-data/"),
          api.get("/supportstaff/"),
          api.get("/salary-advances/", {
            params: {
              academic_year: selectedYear.id,
            },
          }),
        ]);

        if (requestId !== requestCounter.current) {
          return false;
        }

        setTeacherList(getResponseData(teachersResponse));
        setSupportStaffList(getResponseData(staffResponse));
        setData(getResponseData(advancesResponse));

        return true;

      } catch (error) {
        if (requestId !== requestCounter.current) {
          return false;
        }

        console.error(
          "SALARY ADVANCE FETCH ERROR:",
          error?.response?.data || error
        );

        if (showError) {
          Swal.fire({
            title: "Unable to Load Records",
            text: "Salary advance records could not be loaded. Please try again.",
            icon: "error",
            confirmButtonColor: "#082B4C",
          });
        }

        return false;

      } finally {
        if (requestId === requestCounter.current) {
          setFetching(false);
        }
      }
    },
    [selectedYear?.id]
  );


  useEffect(() => {
    fetchInitialData();

    return () => {
      requestCounter.current += 1;
    };
  }, [fetchInitialData]);


  useEffect(() => {
    setData([]);
    setPage(1);
    setReportMonth("");
  }, [selectedYear?.id]);


  // ============================================
  // OPEN AND CLOSE MODAL
  // ============================================

  const handleOpen = () => {
    if (!selectedYear?.id) {
      Swal.fire({
        title: "Academic Year Required",
        text: "Please select an academic year before adding a salary advance.",
        icon: "warning",
        confirmButtonColor: "#082B4C",
      });
      return;
    }

    resetForm();
    setOpen(true);
  };


  const handleClose = () => {
    if (loading) return;

    setOpen(false);
    resetForm();
  };


  // ============================================
  // SAVE / UPDATE
  // ============================================

  const handleSubmit = async () => {
    if (loading) return;

    if (!selectedYear?.id) {
      return Swal.fire(
        "Error",
        "Please select an academic year.",
        "error"
      );
    }

    if (!advance.teacher && !advance.support_staff) {
      return Swal.fire(
        "Staff Required",
        "Select either a teacher or a support staff member.",
        "warning"
      );
    }

    if (advance.teacher && advance.support_staff) {
      return Swal.fire(
        "Invalid Selection",
        "A salary advance can only be assigned to one staff member.",
        "warning"
      );
    }

    if (!advance.month) {
      return Swal.fire(
        "Month Required",
        "Please select a month.",
        "warning"
      );
    }

    const amount = Number(advance.amount);

    if (!Number.isFinite(amount) || amount <= 0) {
      return Swal.fire(
        "Invalid Amount",
        "Enter a valid amount greater than zero.",
        "warning"
      );
    }

    const payload = {
      teacher: advance.teacher?.id || null,
      support_staff: advance.support_staff?.id || null,
      academic_year: selectedYear.id,
      amount,
      reason: advance.reason || "",
      month: advance.month,
    };

    const currentEditId = editId;
    const isUpdating = Boolean(currentEditId);

    setLoading(true);

    try {
      if (isUpdating) {
        await api.put(
          `/salary-advances/${currentEditId}/`,
          payload
        );
      } else {
        await api.post("/salary-advances/", payload);
      }

      setOpen(false);
      resetForm();

      Swal.fire({
        title: "Success",
        text: isUpdating
          ? "Salary advance updated successfully."
          : "Salary advance added successfully.",
        icon: "success",
        confirmButtonColor: "#082B4C",
      });

      await fetchInitialData({ showError: false });

    } catch (error) {
      console.error(
        "SALARY ADVANCE SAVE ERROR:",
        error?.response?.data || error
      );

      Swal.fire({
        title: "Unable to Save",
        text: getErrorMessage(error),
        icon: "error",
        confirmButtonColor: "#082B4C",
      });

    } finally {
      setLoading(false);
    }
  };


  // ============================================
  // EDIT
  // ============================================

  const handleEdit = (item) => {
    if (loading || deletingId) return;

    const teacherId = item?.teacher;
    const supportStaffId = item?.support_staff;

    const selectedTeacher = teacherId
      ? teacherList.find(
          (person) =>
            String(person.id) === String(teacherId)
        ) || {
          id: teacherId,
          full_name: item?.teacher_name || "",
        }
      : null;

    const selectedSupportStaff = supportStaffId
      ? supportStaffList.find(
          (person) =>
            String(person.id) === String(supportStaffId)
        ) || {
          id: supportStaffId,
          full_name: item?.support_staff_name || "",
        }
      : null;

    setAdvance({
      teacher: selectedTeacher,
      support_staff: selectedSupportStaff,
      amount: item?.amount ?? "",
      reason: item?.reason || "",
      month: item?.month || "",
    });

    setEditId(item?.id);
    setOpen(true);
  };


  // ============================================
  // DELETE
  // ============================================

  const handleDelete = async (id) => {
    if (!id || deletingId || loading) return;

    const result = await Swal.fire({
      title: "Delete Salary Advance?",
      text: "This action will permanently delete this salary advance record.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#C0392B",
      cancelButtonColor: "#64748B",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    setDeletingId(id);

    try {
      await api.delete(`/salary-advances/${id}/`);

      setData((previous) =>
        previous.filter(
          (item) => String(item.id) !== String(id)
        )
      );

      Swal.fire({
        title: "Deleted",
        text: "The salary advance record has been deleted.",
        icon: "success",
        confirmButtonColor: "#082B4C",
      });

      await fetchInitialData({ showError: false });

    } catch (error) {
      console.error(
        "SALARY ADVANCE DELETE ERROR:",
        error?.response?.data || error
      );

      Swal.fire({
        title: "Delete Failed",
        text: getErrorMessage(error),
        icon: "error",
        confirmButtonColor: "#082B4C",
      });

    } finally {
      setDeletingId(null);
    }
  };


  // ============================================
  // SEARCH FILTER
  // ============================================

  const filteredData = useMemo(() => {
    const term = filterText.trim().toLowerCase();

    if (!term) return data;

    return data.filter((row) => {
      const searchableValues = [
        row?.teacher_name,
        row?.support_staff_name,
        row?.month,
        row?.status,
        row?.reason,
      ];

      return searchableValues.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(term)
      );
    });
  }, [data, filterText]);


  // ============================================
  // MONTHLY REPORT DATA
  // ============================================

  const monthlyReportRecords = useMemo(() => {
    if (!reportMonth) return [];

    return data.filter(
      (row) =>
        String(row?.month || "").toLowerCase() ===
        reportMonth.toLowerCase()
    );
  }, [data, reportMonth]);


  const summary = useMemo(
    () =>
      data.reduce(
        (result, row) => {
          result.totalAdvances += toNumber(row?.amount);
          result.totalDeducted += toNumber(row?.total_deducted);
          result.totalBalance += toNumber(row?.remaining_balance);

          return result;
        },
        {
          totalAdvances: 0,
          totalDeducted: 0,
          totalBalance: 0,
        }
      ),
    [data]
  );


  const reportSummary = useMemo(
    () =>
      monthlyReportRecords.reduce(
        (result, row) => {
          result.totalAdvances += toNumber(row?.amount);
          result.totalDeducted += toNumber(row?.total_deducted);
          result.totalBalance += toNumber(row?.remaining_balance);

          return result;
        },
        {
          totalAdvances: 0,
          totalDeducted: 0,
          totalBalance: 0,
        }
      ),
    [monthlyReportRecords]
  );


  // ============================================
  // DOWNLOAD MONTHLY PDF
  // ============================================

  const handleDownloadPDF = async () => {
    if (downloading) return;

    if (!selectedYear?.id) {
      return Swal.fire(
        "Academic Year Required",
        "Please select an academic year before downloading the report.",
        "warning"
      );
    }

    if (!reportMonth) {
      return Swal.fire(
        "Select a Month",
        "Please select the month you want to download.",
        "warning"
      );
    }

    if (fetching) {
      return Swal.fire(
        "Please Wait",
        "Records are still loading. Please try again shortly.",
        "info"
      );
    }

    if (monthlyReportRecords.length === 0) {
      return Swal.fire(
        "No Records Found",
        `There are no salary advances recorded for ${reportMonth} in ${academicYearName}.`,
        "info"
      );
    }

    setDownloading(true);

    let fileUrl;

    try {
      const generatedAt = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });

      const pdfDocument = (
        <SalaryAdvancePDF
          records={monthlyReportRecords}
          month={reportMonth}
          academicYear={academicYearName}
          generatedAt={generatedAt}
        />
      );

      const blob = await pdf(pdfDocument).toBlob();

      if (!blob || blob.size === 0) {
        throw new Error("The generated PDF file is empty.");
      }

      fileUrl = URL.createObjectURL(blob);

      const link = window.document.createElement("a");

      link.href = fileUrl;

      link.download = `Salary_Advance_${reportMonth}_${String(
        academicYearName
      ).replace(/[^a-zA-Z0-9-_]/g, "_")}.pdf`;

      window.document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        title: "PDF Ready",
        text: `${monthlyReportRecords.length} salary advance record(s) included in the ${reportMonth} report.`,
        icon: "success",
        confirmButtonColor: "#082B4C",
      });

    } catch (error) {
      console.error("SALARY ADVANCE PDF ERROR:", error);

      Swal.fire({
        title: "PDF Generation Failed",
        text: "The salary advance report could not be generated. Please try again.",
        icon: "error",
        confirmButtonColor: "#082B4C",
      });

    } finally {
      if (fileUrl) {
        window.setTimeout(() => {
          URL.revokeObjectURL(fileUrl);
        }, 30000);
      }

      setDownloading(false);
    }
  };


  // ============================================
  // STAFF OPTIONS
  // ============================================

  const teacherOptions = teacherList.map((person) => ({
    value: person.id,
    label:
      person.full_name ||
      person.name ||
      person.username ||
      `Teacher #${person.id}`,
  }));

  const supportStaffOptions = supportStaffList.map((person) => ({
    value: person.id,
    label:
      person.full_name ||
      person.name ||
      person.username ||
      `Staff #${person.id}`,
  }));


  // ============================================
  // TABLE COLUMNS
  // ============================================

  const columns = [
    {
      title: "#",
      key: "number",
      width: 55,
      render: (_, __, index) =>
        (page - 1) * ROWS_PER_PAGE + index + 1,
    },

    {
      title: "STAFF MEMBER",
      key: "staff",
      width: 190,
      render: (_, row) => (
        <Space size={9}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "#EAF1F8",
              color: "#082B4C",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {row?.teacher ? (
              <UserOutlined />
            ) : (
              <TeamOutlined />
            )}
          </div>

          <div style={{ minWidth: 0 }}>
            <Text strong style={{ display: "block" }}>
              {getStaffName(row)}
            </Text>

            <Text type="secondary" style={{ fontSize: 11 }}>
              {getStaffType(row)}
            </Text>
          </div>
        </Space>
      ),
    },

    {
      title: "ADVANCE AMOUNT",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (value) => (
        <Text strong>{formatMoney(value)}</Text>
      ),
    },

    {
      title: "AMOUNT TAKEN",
      dataIndex: "total_deducted",
      key: "total_deducted",
      align: "right",
      render: (value) => formatMoney(value),
    },

    {
      title: "REMAINING BALANCE",
      dataIndex: "remaining_balance",
      key: "remaining_balance",
      align: "right",
      render: (value) => (
        <Text
          strong
          style={{
            color:
              toNumber(value) > 0
                ? "#D97706"
                : "#15803D",
          }}
        >
          {formatMoney(value)}
        </Text>
      ),
    },

    {
      title: "MONTH",
      dataIndex: "month",
      key: "month",
      width: 105,
      render: (value) => value || "-",
    },

    {
      title: "STATUS",
      key: "status",
      width: 125,
      render: (_, row) => {
        const status = getAdvanceStatus(row);

        const isCompleted =
          String(status).toLowerCase() === "fully taken";

        return (
          <Tag color={isCompleted ? "success" : "warning"}>
            {status}
          </Tag>
        );
      },
    },

    {
      title: "REASON",
      dataIndex: "reason",
      key: "reason",
      width: 190,
      render: (value) => (
        <Tooltip title={value || "No reason provided"}>
          <Text
            type="secondary"
            ellipsis
            style={{
              maxWidth: 170,
              display: "block",
            }}
          >
            {value || "-"}
          </Text>
        </Tooltip>
      ),
    },

    {
      title: "ACTIONS",
      key: "actions",
      fixed: "right",
      width: 125,
      render: (_, row) => (
        <Space size={5}>
          <Tooltip title="Edit advance">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(row)}
              disabled={loading || Boolean(deletingId)}
              style={{ color: "#075985" }}
            />
          </Tooltip>

          <Tooltip title="Delete advance">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(row.id)}
              loading={deletingId === row.id}
              disabled={
                loading ||
                (Boolean(deletingId) && deletingId !== row.id)
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];


  // ============================================
  // RENDER
  // ============================================

  return (
    <div
      style={{
        minHeight: "100%",
        padding: 20,
        background: "#F4F7FB",
      }}
    >

      {/* PAGE HEADER */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 14,
          marginBottom: 22,
        }}
      >
        <div>
          <Title
            level={3}
            style={{
              margin: 0,
              color: "#082B4C",
              fontWeight: 750,
            }}
          >
            Salary Advances
          </Title>

          <Text type="secondary">
            Manage staff salary advances and monthly deductions.
          </Text>

          <div style={{ marginTop: 7 }}>
            <Tag color="blue">
              Academic Year: {academicYearName}
            </Tag>
          </div>
        </div>

        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchInitialData()}
            loading={fetching}
          >
            Refresh
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpen}
            disabled={!selectedYear?.id || loading}
            style={{
              background: "#082B4C",
              borderColor: "#082B4C",
              height: 40,
              fontWeight: 600,
            }}
          >
            Add Salary Advance
          </Button>
        </Space>
      </div>


      {!selectedYear?.id && (
        <Alert
          type="warning"
          showIcon
          message="No academic year selected"
          description="Select an academic year to view and manage salary advances."
          style={{ marginBottom: 18 }}
        />
      )}


      {/* SUMMARY CARDS */}

      <Row gutter={[14, 14]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 3px 14px rgba(20,45,75,0.05)",
            }}
          >
            <Statistic
              title="Total Advances"
              value={summary.totalAdvances}
              precision={2}
              prefix="$"
              valueStyle={{
                color: "#082B4C",
                fontWeight: 700,
              }}
            />

            <Text type="secondary" style={{ fontSize: 11 }}>
              All records in this academic year
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 3px 14px rgba(20,45,75,0.05)",
            }}
          >
            <Statistic
              title="Total Amount Taken"
              value={summary.totalDeducted}
              precision={2}
              prefix="$"
              valueStyle={{
                color: "#15803D",
                fontWeight: 700,
              }}
            />

            <Text type="secondary" style={{ fontSize: 11 }}>
              Amount already deducted
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card
            bordered={false}
            style={{
              borderRadius: 12,
              boxShadow: "0 3px 14px rgba(20,45,75,0.05)",
            }}
          >
            <Statistic
              title="Outstanding Balance"
              value={summary.totalBalance}
              precision={2}
              prefix="$"
              valueStyle={{
                color: "#D97706",
                fontWeight: 700,
              }}
            />

            <Text type="secondary" style={{ fontSize: 11 }}>
              Remaining staff advance balances
            </Text>
          </Card>
        </Col>
      </Row>


      {/* MONTHLY PDF DOWNLOAD */}

      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          marginBottom: 20,
          boxShadow: "0 3px 14px rgba(20,45,75,0.05)",
        }}
      >
        <Row gutter={[16, 14]} align="middle">

          <Col xs={24} lg={9}>
            <Space align="center" size={12}>
              <div
                style={{
                  width: 43,
                  height: 43,
                  borderRadius: 12,
                  background: "#EAF1F8",
                  color: "#082B4C",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                }}
              >
                <FilePdfOutlined />
              </div>

              <div>
                <Text
                  strong
                  style={{
                    display: "block",
                    color: "#082B4C",
                  }}
                >
                  Monthly Salary Advance Report
                </Text>

                <Text
                  type="secondary"
                  style={{ fontSize: 12 }}
                >
                  Download all advances recorded for a selected month.
                </Text>
              </div>
            </Space>
          </Col>


          <Col xs={24} sm={12} lg={7}>
            <Select
              placeholder="Select reporting month"
              value={reportMonth || undefined}
              onChange={setReportMonth}
              options={MONTH_OPTIONS.map((month) => ({
                value: month,
                label: month,
              }))}
              style={{ width: "100%" }}
              size="large"
              allowClear
              disabled={fetching || !selectedYear?.id}
            />
          </Col>


          <Col xs={24} sm={12} lg={8}>
            <Space
              direction="vertical"
              size={3}
              style={{ width: "100%" }}
            >
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadPDF}
                loading={downloading}
                disabled={
                  !reportMonth ||
                  fetching ||
                  !selectedYear?.id
                }
                size="large"
                block
                style={{
                  background: "#082B4C",
                  borderColor: "#082B4C",
                  fontWeight: 600,
                }}
              >
                {downloading
                  ? "Generating PDF..."
                  : "Download Monthly PDF"}
              </Button>

              {reportMonth && (
                <Text
                  type="secondary"
                  style={{ fontSize: 11 }}
                >
                  {monthlyReportRecords.length} record(s) found for {reportMonth}
                </Text>
              )}
            </Space>
          </Col>

        </Row>


        {reportMonth && monthlyReportRecords.length > 0 && (
          <Row gutter={[12, 8]} style={{ marginTop: 17 }}>

            <Col xs={24} sm={8}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                MONTHLY ADVANCES
              </Text>

              <div>
                <Text strong style={{ color: "#082B4C" }}>
                  {formatMoney(reportSummary.totalAdvances)}
                </Text>
              </div>
            </Col>

            <Col xs={24} sm={8}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                AMOUNT TAKEN
              </Text>

              <div>
                <Text strong style={{ color: "#15803D" }}>
                  {formatMoney(reportSummary.totalDeducted)}
                </Text>
              </div>
            </Col>

            <Col xs={24} sm={8}>
              <Text type="secondary" style={{ fontSize: 11 }}>
                REMAINING BALANCE
              </Text>

              <div>
                <Text strong style={{ color: "#D97706" }}>
                  {formatMoney(reportSummary.totalBalance)}
                </Text>
              </div>
            </Col>

          </Row>
        )}
      </Card>


      {/* SALARY ADVANCE TABLE */}

      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          boxShadow: "0 3px 14px rgba(20,45,75,0.05)",
        }}
        bodyStyle={{ padding: 18 }}
      >

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: 17,
          }}
        >
          <div>
            <Title
              level={5}
              style={{
                margin: 0,
                color: "#082B4C",
              }}
            >
              Salary Advance Records
            </Title>

            <Text
              type="secondary"
              style={{ fontSize: 12 }}
            >
              {filteredData.length} record(s) found
            </Text>
          </div>

          <Input
            allowClear
            prefix={
              <SearchOutlined style={{ color: "#94A3B8" }} />
            }
            placeholder="Search staff, month, status or reason..."
            value={filterText}
            onChange={(event) => {
              setFilterText(event.target.value);
              setPage(1);
            }}
            style={{
              width: 330,
              maxWidth: "100%",
              borderRadius: 8,
            }}
            size="large"
          />
        </div>


        <Spin
          spinning={fetching}
          tip="Loading salary advance records..."
        >
          <Table
            rowKey={(row) => row.id}
            columns={columns}
            dataSource={filteredData}
            scroll={{ x: 1250 }}
            size="middle"
            pagination={{
              current: page,
              pageSize: ROWS_PER_PAGE,
              total: filteredData.length,
              showSizeChanger: false,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
              onChange: (newPage) => setPage(newPage),
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    fetching
                      ? "Loading salary advances..."
                      : filterText
                        ? "No matching salary advance records"
                        : "No salary advance records found"
                  }
                />
              ),
            }}
          />
        </Spin>

      </Card>


      {/* ADD / EDIT MODAL */}

      <Modal
        title={
          <div>
            <Text
              strong
              style={{
                display: "block",
                color: "#082B4C",
                fontSize: 17,
              }}
            >
              {editId
                ? "Edit Salary Advance"
                : "Add Salary Advance"}
            </Text>

            <Text
              type="secondary"
              style={{ fontSize: 12 }}
            >
              {editId
                ? "Update the staff member's advance details."
                : "Enter the details of the staff salary advance."}
            </Text>
          </div>
        }
        open={open}
        onCancel={handleClose}
        footer={null}
        width={560}
        destroyOnClose
        maskClosable={!loading}
        closable={!loading}
      >

        <div style={{ paddingTop: 12 }}>

          <Alert
            type="info"
            showIcon
            message={`Academic Year: ${academicYearName}`}
            style={{ marginBottom: 20 }}
          />


          {/* TEACHER */}

          <div style={{ marginBottom: 17 }}>
            <Text
              strong
              style={{
                display: "block",
                marginBottom: 7,
              }}
            >
              Teacher
            </Text>

            <Select
              showSearch
              allowClear
              placeholder="Search and select a teacher"
              value={advance.teacher?.id ?? undefined}
              options={teacherOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value) => {
                const selected = teacherList.find(
                  (person) =>
                    String(person.id) === String(value)
                );

                setAdvance((previous) => ({
                  ...previous,
                  teacher: selected || null,
                  support_staff: null,
                }));
              }}
              disabled={
                Boolean(advance.support_staff) || loading
              }
              style={{ width: "100%" }}
              size="large"
            />

            {advance.support_staff && (
              <Text
                type="secondary"
                style={{ fontSize: 11 }}
              >
                Clear the support staff selection to choose a teacher.
              </Text>
            )}
          </div>


          {/* SUPPORT STAFF */}

          <div style={{ marginBottom: 17 }}>
            <Text
              strong
              style={{
                display: "block",
                marginBottom: 7,
              }}
            >
              Support Staff
            </Text>

            <Select
              showSearch
              allowClear
              placeholder="Search and select support staff"
              value={advance.support_staff?.id ?? undefined}
              options={supportStaffOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value) => {
                const selected = supportStaffList.find(
                  (person) =>
                    String(person.id) === String(value)
                );

                setAdvance((previous) => ({
                  ...previous,
                  support_staff: selected || null,
                  teacher: null,
                }));
              }}
              disabled={
                Boolean(advance.teacher) || loading
              }
              style={{ width: "100%" }}
              size="large"
            />

            {advance.teacher && (
              <Text
                type="secondary"
                style={{ fontSize: 11 }}
              >
                Clear the teacher selection to choose support staff.
              </Text>
            )}
          </div>


          {/* AMOUNT AND MONTH */}

          <Row gutter={14}>

            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 17 }}>
                <Text
                  strong
                  style={{
                    display: "block",
                    marginBottom: 7,
                  }}
                >
                  Advance Amount ($)
                </Text>

                <InputNumber
                  min={0.01}
                  step={0.01}
                  precision={2}
                  placeholder="Enter amount"
                  value={
                    advance.amount === ""
                      ? null
                      : Number(advance.amount)
                  }
                  onChange={(value) =>
                    setAdvance((previous) => ({
                      ...previous,
                      amount: value ?? "",
                    }))
                  }
                  disabled={loading}
                  style={{ width: "100%" }}
                  size="large"
                />
              </div>
            </Col>


            <Col xs={24} sm={12}>
              <div style={{ marginBottom: 17 }}>
                <Text
                  strong
                  style={{
                    display: "block",
                    marginBottom: 7,
                  }}
                >
                  Month
                </Text>

                <Select
                  placeholder="Select month"
                  value={advance.month || undefined}
                  onChange={(value) =>
                    setAdvance((previous) => ({
                      ...previous,
                      month: value || "",
                    }))
                  }
                  options={MONTH_OPTIONS.map((month) => ({
                    value: month,
                    label: month,
                  }))}
                  disabled={loading}
                  style={{ width: "100%" }}
                  size="large"
                />
              </div>
            </Col>

          </Row>


          {/* REASON */}

          <div style={{ marginBottom: 20 }}>
            <Text
              strong
              style={{
                display: "block",
                marginBottom: 7,
              }}
            >
              Reason
            </Text>

            <TextArea
              placeholder="Enter the reason for this salary advance..."
              rows={4}
              value={advance.reason}
              onChange={(event) =>
                setAdvance((previous) => ({
                  ...previous,
                  reason: event.target.value,
                }))
              }
              disabled={loading}
              maxLength={1000}
              showCount
            />
          </div>


          {/* MODAL BUTTONS */}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              borderTop: "1px solid #E8EDF3",
              paddingTop: 17,
            }}
          >
            <Button
              onClick={handleClose}
              disabled={loading}
              size="large"
            >
              Cancel
            </Button>

            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              size="large"
              style={{
                minWidth: 140,
                background: "#082B4C",
                borderColor: "#082B4C",
                fontWeight: 600,
              }}
            >
              {editId ? "Update Advance" : "Save Advance"}
            </Button>
          </div>

        </div>

      </Modal>

    </div>
  );
};

export default SalaryAdvance;
